import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import { ShieldCheck, Cpu, Search, CheckCircle2, XCircle, PauseCircle, Key, User, Trash2 } from 'lucide-react';
import CopyButton from '../../components/common/CopyButton';

const AccountApprovals = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Selected account for admin review modal
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  // Admin Delete Account state
  const [accountToDeleteByAdmin, setAccountToDeleteByAdmin] = useState(null);
  const [deletingAdmin, setDeletingAdmin] = useState(false);

  // Initial Balance Fetch & Approval state
  const [fetchingBalance, setFetchingBalance] = useState(false);
  const [liveBalance, setLiveBalance] = useState(null);
  const [approvingBalance, setApprovingBalance] = useState(false);

  const fetchAdminAccounts = async () => {
    try {
      const res = await axiosClient.get('/mt5/admin/all');
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('[Admin MT5 Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminAccounts();
  }, []);

  const handleAdminDeleteAccount = async () => {
    if (!accountToDeleteByAdmin) return;
    setDeletingAdmin(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.delete(`/mt5/admin/${accountToDeleteByAdmin._id}`);
      setFeedbackMsg({ type: 'success', text: `MT5 Account #${accountToDeleteByAdmin.accountNumber} deleted successfully by admin.` });
      setAccountToDeleteByAdmin(null);
      if (selectedAccount?._id === accountToDeleteByAdmin._id) {
        setSelectedAccount(null);
      }
      fetchAdminAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete MT5 account.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setDeletingAdmin(false);
    }
  };

  const handleFetchInitialBalance = async () => {
    if (!selectedAccount) return;
    setFetchingBalance(true);
    setFeedbackMsg({ type: '', text: '' });
    try {
      const res = await axiosClient.post(`/mt5/admin/${selectedAccount._id}/fetch-balance`);
      setLiveBalance(res.data.liveBalance);
      setFeedbackMsg({ type: 'success', text: `Fetched live balance from MT5: $${res.data.liveBalance}` });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to fetch live balance from provider.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setFetchingBalance(false);
    }
  };

  const handleApproveInitialBalance = async () => {
    if (!selectedAccount || liveBalance === null) return;
    setApprovingBalance(true);
    setFeedbackMsg({ type: '', text: '' });
    try {
      await axiosClient.post(`/mt5/admin/${selectedAccount._id}/approve-initial-balance`, {
        initialBalance: liveBalance
      });
      setFeedbackMsg({ type: 'success', text: `Initial balance of $${liveBalance} approved successfully!` });
      setSelectedAccount(prev => prev ? { ...prev, initialBalance: liveBalance, isInitialBalanceApproved: true } : null);
      fetchAdminAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to approve initial balance.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setApprovingBalance(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedAccount) return;
    setUpdating(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.patch(`/mt5/admin/${selectedAccount._id}/status`, {
        status: newStatus,
        approvalNotes
      });
      setFeedbackMsg({ type: 'success', text: `Account ${selectedAccount.accountNumber} marked as ${newStatus}.` });
      setSelectedAccount(null);
      setApprovalNotes('');
      fetchAdminAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update account status.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateCopier = async (newCopierStatus) => {
    if (!selectedAccount) return;
    setUpdating(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.patch(`/mt5/admin/${selectedAccount._id}/copier`, {
        copierStatus: newCopierStatus
      });
      setFeedbackMsg({ type: 'success', text: `Copier status set to ${newCopierStatus}.` });
      setSelectedAccount(null);
      fetchAdminAccounts();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update copier status.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setUpdating(false);
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    const matchSearch = 
      acc.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.server?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = statusFilter === 'all' || acc.status === statusFilter;

    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Admin Title Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-5 h-5 text-[#F97316]" />
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">MT5 Accounts Verification & Copier Panel</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Review user MT5 credentials, approve/reject accounts, and connect to client's xalgos.in trade copier.
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search user, account, server..."
              className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#F97316]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#09090B] border border-[#27272A] text-[#FFFFFF] text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#F97316]"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Admin Accounts Table */}
      <Card title={`Submitted MT5 Accounts (${filteredAccounts.length})`} subtitle="Click on any row to verify credentials and update approval/copier status">
        {loading ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">Loading submitted accounts...</div>
        ) : filteredAccounts.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">No MT5 accounts match the filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">MT5 Account ID</th>
                  <th className="py-3 px-4">Broker Server</th>
                  <th className="py-3 px-4">Approval Status</th>
                  <th className="py-3 px-4">xalgos Copier</th>
                  <th className="py-3 px-4">Initial Balance</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {filteredAccounts.map((acc) => (
                  <tr key={acc._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center space-x-1.5">
                        <span className="font-bold text-[#FFFFFF]">{acc.userId?.name || 'Unknown'}</span>
                        <span className="font-mono text-[10px] text-[#F59E0B]">({acc.userId?.userIdTag})</span>
                        <CopyButton text={acc.userId?.userIdTag} />
                      </div>
                      <div className="flex items-center space-x-1.5 mt-0.5">
                        <span className="text-[10px] text-[#9CA3AF]">{acc.userId?.email}</span>
                        <CopyButton text={acc.userId?.email} />
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center space-x-1.5 font-mono font-bold text-[#FFFFFF]">
                        <span>{acc.accountNumber}</span>
                        <CopyButton text={acc.accountNumber} />
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center space-x-1.5 text-[#9CA3AF]">
                        <span>{acc.server}</span>
                        <CopyButton text={acc.server} />
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={acc.status} label={acc.status} />
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge 
                        status={acc.copierStatus === 'connected' ? 'approved' : acc.copierStatus === 'paused' ? 'pending' : 'disabled'} 
                        label={`Copier: ${acc.copierStatus}`} 
                      />
                    </td>
                    <td className="py-3.5 px-4">
                      {acc.isInitialBalanceApproved ? (
                        <span className="font-mono font-extrabold text-[#10B981]">${acc.initialBalance?.toFixed(2)}</span>
                      ) : (
                        <span className="text-[10px] text-[#F59E0B] font-semibold">Not Approved</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedAccount(acc);
                            setLiveBalance(null);
                            setApprovalNotes(acc.approvalNotes || '');
                          }}
                        >
                          Review Account
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setAccountToDeleteByAdmin(acc)}
                          className="flex items-center space-x-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Delete</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Account Review Modal */}
      {selectedAccount && (
        <div className="fixed inset-0 z-50 !mt-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF]">Verify MT5 Account Credentials</h3>
                <div className="flex items-center space-x-1.5 text-xs text-[#9CA3AF] mt-0.5">
                  <span>Account ID:</span>
                  <span className="font-mono text-[#F59E0B] font-bold">{selectedAccount.accountNumber}</span>
                  <CopyButton text={selectedAccount.accountNumber} />
                </div>
              </div>
              <button onClick={() => setSelectedAccount(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3 p-4 bg-[#09090B] rounded-2xl border border-[#27272A] text-xs">
              <div>
                <span className="text-[#9CA3AF] block text-[10px] uppercase mb-0.5">User Profile</span>
                <div className="flex items-center space-x-1">
                  <span className="font-bold text-[#FFFFFF]">{selectedAccount.userId?.name} ({selectedAccount.userId?.email})</span>
                  <CopyButton text={selectedAccount.userId?.email} />
                </div>
              </div>
              <div>
                <span className="text-[#9CA3AF] block text-[10px] uppercase mb-0.5">Broker Server</span>
                <div className="flex items-center space-x-1">
                  <span className="font-bold text-[#FFFFFF]">{selectedAccount.server}</span>
                  <CopyButton text={selectedAccount.server} />
                </div>
              </div>
              <div>
                <span className="text-[#9CA3AF] block text-[10px] uppercase mb-0.5">Password</span>
                <div className="flex items-center space-x-1">
                  <span className="font-mono font-bold text-[#F97316]">{selectedAccount.investorPassword}</span>
                  <CopyButton text={selectedAccount.investorPassword} />
                </div>
              </div>
              <div>
                <span className="text-[#9CA3AF] block text-[10px] uppercase mb-0.5">Current Approval</span>
                <StatusBadge status={selectedAccount.status} label={selectedAccount.status} />
              </div>
            </div>

            {/* Approval Notes Input */}
            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Admin Approval / Rejection Notes</label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Enter notes for client (e.g. Account verified successfully, or password incorrect)"
                rows={2}
                className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs focus:outline-none focus:border-[#F97316]"
              />
            </div>

            {/* Actions Section */}
            <div className="space-y-3 pt-3 border-t border-[#27272A]">
              <span className="text-[11px] font-bold text-[#9CA3AF] uppercase block">1. Account Approval Actions</span>
              <div className="grid grid-cols-3 gap-2">
                <Button 
                  variant="success" 
                  size="sm" 
                  onClick={() => handleUpdateStatus('approved')}
                  disabled={updating}
                >
                  Approve Account
                </Button>
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={() => handleUpdateStatus('rejected')}
                  disabled={updating}
                >
                  Reject Account
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => handleUpdateStatus('disabled')}
                  disabled={updating}
                >
                  Disable Account
                </Button>
              </div>

              {selectedAccount.status === 'approved' && (
                <div className="pt-3 border-t border-[#27272A] space-y-3">
                  {/* 2. Initial MT5 Balance Verification & Approval */}
                  <div className="space-y-2">
                    <span className="text-[11px] font-bold text-[#9CA3AF] uppercase block">2. Initial MT5 Balance Verification</span>
                    
                    {selectedAccount.isInitialBalanceApproved ? (
                      <div className="p-3 bg-[#10B981]/15 border border-[#10B981]/40 rounded-xl flex items-center justify-between text-xs text-[#10B981]">
                        <span className="font-semibold">✓ Initial Balance Approved</span>
                        <span className="font-mono font-extrabold text-sm">${selectedAccount.initialBalance?.toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="space-y-2.5 bg-[#09090B] p-3.5 rounded-xl border border-[#27272A]">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#9CA3AF]">Live Provider Balance:</span>
                          <span className="font-mono font-bold text-sm text-[#F59E0B]">
                            {liveBalance !== null ? `$${Number(liveBalance).toFixed(2)}` : 'Not Fetched Yet'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleFetchInitialBalance}
                            disabled={fetchingBalance}
                          >
                            {fetchingBalance ? 'Fetching...' : 'Fetch Initial Balance'}
                          </Button>

                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleApproveInitialBalance}
                            disabled={approvingBalance || liveBalance === null}
                          >
                            {approvingBalance ? 'Approving...' : 'Approve Balance'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 3. xalgos Trade Copier Status */}
                  <div className="pt-3 border-t border-[#27272A] space-y-3">
                    <span className="text-[11px] font-bold text-[#9CA3AF] uppercase block">3. xalgos Trade Copier Status</span>
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={() => handleUpdateCopier('connected')}
                        disabled={updating}
                      >
                        Connect Copier
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleUpdateCopier('paused')}
                        disabled={updating}
                      >
                        Pause Copier
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => handleUpdateCopier('disconnected')}
                        disabled={updating}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* 4. Permanent Account Deletion */}
              <div className="pt-3 border-t border-[#27272A] space-y-2">
                <span className="text-[11px] font-bold text-[#EF4444] uppercase block">4. Danger Zone</span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setAccountToDeleteByAdmin(selectedAccount)}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete MT5 Account Permanently</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Delete Confirmation Modal */}
      {accountToDeleteByAdmin && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#EF4444]/40 rounded-3xl p-6 shadow-card space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                <Trash2 className="w-5 h-5 text-[#EF4444]" />
                <span>Admin Delete MT5 Account</span>
              </h3>
              <button onClick={() => setAccountToDeleteByAdmin(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="space-y-2.5 text-xs text-[#9CA3AF]">
              <p>
                Are you sure you want to delete MT5 Account <span className="font-mono font-bold text-[#FFFFFF]">#{accountToDeleteByAdmin.accountNumber}</span>?
              </p>
              <div className="p-3.5 bg-[#09090B] rounded-xl border border-[#27272A] space-y-1">
                <p><span className="text-[#9CA3AF]">User:</span> <span className="font-bold text-white">{accountToDeleteByAdmin.userId?.name} ({accountToDeleteByAdmin.userId?.email})</span></p>
                <p><span className="text-[#9CA3AF]">Server:</span> <span className="font-semibold text-white">{accountToDeleteByAdmin.server}</span></p>
                <p><span className="text-[#9CA3AF]">Approval Status:</span> <span className="font-semibold text-white capitalize">{accountToDeleteByAdmin.status}</span></p>
                <p><span className="text-[#9CA3AF]">Copier Status:</span> <span className="font-semibold text-white capitalize">{accountToDeleteByAdmin.copierStatus}</span></p>
              </div>
              <p className="text-[#EF4444] font-bold">
                ⚠️ Danger: This will permanently purge this MT5 account entry from MongoDB database.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-3 border-t border-[#27272A]">
              <Button variant="secondary" size="sm" onClick={() => setAccountToDeleteByAdmin(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleAdminDeleteAccount} disabled={deletingAdmin}>
                {deletingAdmin ? 'Deleting...' : 'Confirm Admin Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountApprovals;
